'use client';

import { TextCombination } from "@/components/_rebrand/TextCombination";
import cn from "classnames";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { FC } from "react";
import { ButtonBaseClasses } from "../Button";
import { TertiaryButton } from "../Button/TertiaryButton";

export type FeatureCardProps = {
  title?: string;
  description?: string;
  href?: string;
  target?: string;
  buttonText?: string;
  imageSrc?: string;
};

const MotionLink = motion(Link);

/**
 * When an href is provided, this card acts as a link: the entire card is
 * clickable and hovering it shows the highlight and animates the button
 * arrow (via framer-motion variant propagation). Without an href the card
 * renders as a static tile with no hover effects.
 */
export const FeatureCard: FC<FeatureCardProps> = ({ title, description, href, target = '_self', buttonText, imageSrc }) => {
  const Card = href ? MotionLink : motion.div;

  return (
    <Card
      {...(href ? { href, target } : {})}
      initial="rest"
      whileHover={href ? 'hover' : undefined}
      className={cn(
        "block w-full h-full surface-l1 rounded-12 transition-all duration-400 ease-in-out",
        href && "hover:card-hover-highlight"
      )}
    >
      {imageSrc && (
        <div className="p-2xs">
          <Image src={imageSrc} alt={title || ''} width={400} height={240} className="rounded-8 w-full h-full object-cover" />
        </div>
      )}
      <div className={cn("flex flex-col gap-y-4 p-xl", imageSrc && "pt-s")}>
        {title && (
          <TextCombination
            type="h3p2"
            headingLevel="h3"
            align="left"
            title={title}
            description={description}
          />
        )}
        {buttonText && (
          <div>
            <TertiaryButton
              inheritVariants={!!href}
              animate={href ? undefined : 'hover'}
              className={cn(ButtonBaseClasses, "flex items-center justify-center button-text-inverted space-x-xs")}
            >
              {buttonText}
            </TertiaryButton>
          </div>
        )}
      </div>
    </Card>
  );
};
